export type ReportStatus = "SUBMITTED" | "IN_REVIEW" | "APPROVED" | "UPLOADED" | "REJECTED" | "PENDING";
export type ItemStatus = "PENDING" | "APPROVED" | "UPLOADED" | "REJECTED";

export const ReportStatusValue = {
    Submitted: "SUBMITTED",
    InReview: "IN_REVIEW",
    Approved: "APPROVED",
    Uploaded: "UPLOADED",
    Rejected: "REJECTED",
    Pending: "PENDING",
} as const satisfies Record<string, ReportStatus>;

export const ItemStatusValue = {
    Pending: "PENDING",
    Approved: "APPROVED",
    Uploaded: "UPLOADED",
    Rejected: "REJECTED",
} as const satisfies Record<string, ItemStatus>;

export const enum ExpenseAction {
    Approve = "approve",
    Deny = "deny",
    Pending = "pending",
}

export type ReportAction = "approve" | "send_back";
export const ReportActionValue = {
    Approve: "approve",
    SendBack: "send_back",
} as const satisfies Record<string, ReportAction>;

export interface Submitter {
    firstName: string;
    lastName: string;
    email: string;
}

export interface ViolationAction {
    type: string;
}

export interface PolicyViolation {
    violationId: string;
    policyName: string;
    policyType?: string | null;
    status: string;
    actions: ViolationAction[];
    justification: string | null;
}

export interface ExpenseState {
    expenseId: string;
    state: ItemStatus;
}

export interface LostReceiptDeclaration {
    declared: boolean;
    initials: string | null;
    explanation: string | null;
    declaredAt: string | null;
}

export interface ExpenseReport {
    id: string;
    submitterUserId?: string | null;
    reportNumber: string;
    reportName: string;
    submitter: Submitter;
    state: ReportStatus;
    // Controlled by data layer; UI components should only consume this flag.
    isEditable: boolean;
    // Controlled by data layer; preview reports should not expose expense details.
    canViewExpenses: boolean;
    itemCount: number;
    totalAmount: number;
    currencyCode?: string;
    submittedAt: string;
    expenseStates?: ExpenseState[];
}

export interface ExpenseItem {
    id: string;
    reportId: string;
    submitterUserId?: string | null;
    submitter: Submitter;
    // Controlled by data layer; UI components should only consume this flag.
    isEditable: boolean;
    merchant: string;
    category: string;
    subCategoryId?: number | null;
    hasReceipt: boolean;
    receiptMissing: boolean;
    settleDate: string;
    chargeDate: string;
    exception: string | null;
    violations: PolicyViolation[];
    isPersonal: boolean;
    reimbursable?: boolean | null;
    hasBillable?: boolean | null;
    lastFour: string | null;
    amount: number;
    currencyCode?: string;
    status: ItemStatus;
    accountCode?: string | null;
    paymentMethod: string | null;
    description: string | null;
    chargeAmount: number;
    cardCurrencyAmount: number;
    generatedBy?: string | null;
    lostReceiptDeclaration?: LostReceiptDeclaration | null;
}

// ─── Full Expense Detail (getReportFullExpenses query) ───────────────────────

export interface FullExpenseReceiptMappedFields {
    merchant: string | null;
    expenseDate: string | null;
    amount: number | null;
    currencyCode: string | null;
}

export interface FullExpenseReceipt {
    receiptId: string;
    fileType: string | null;
    s3URL: string | null;
    receiptSource: string | null;
    mappedFields: FullExpenseReceiptMappedFields | null;
}

/** A single expense item nested inside the `getReportFullExpenses` response.
 * `reportNumber` and `submittedBy` are hoisted from the wrapper by the data layer. */
export interface FullExpenseDetail {
    id: string;
    reportId: string;
    reportState?: ReportStatus | null;
    reportNumber: string;
    submittedBy: string;
    submittedAt?: string | null;
    submitter: Submitter;
    isEditable: boolean;
    merchant: string;
    category: string;
    subCategoryId?: number | null;
    hasReceipt: boolean;
    receiptMissing: boolean;
    settleDate: string;
    chargeDate: string;
    expenseDate: string;
    exception: string | null;
    violations: PolicyViolation[];
    isPersonal: boolean;
    reimbursable?: boolean | null;
    hasBillable?: boolean | null;
    lastFour: string | null;
    amount: number;
    status: ItemStatus;
    accountCode: string | null;
    paymentMethod: { method: string; cardKey: string | null; display: { brand: string | null; last4: string | null; description: string | null } } | null;
    description: string | null;
    chargeAmount: number;
    cardCurrencyAmount: number;
    currencyCode?: string;
    cardCurrencyCode?: string;
    location?: string | null;
    locationFrom?: string | null;
    locationTo?: string | null;
    participants?: string[] | null;
    client?: string | null;
    departureDate?: string | null;
    ticketNumber?: string | null;
    checkInDate?: string | null;
    pickUpDate?: string | null;
    pickupAddress?: string | null;
    dropoffAddress?: string | null;
    milageLocationFrom?: string | null;
    milageLocationTo?: string | null;
    distance?: string | null;
    rate?: string | null;
    distanceRate?: string | null;
    invoiceNumber?: string | null;
    generatedBy?: string | null;
    receipts: FullExpenseReceipt[];
    lostReceiptDeclaration?: LostReceiptDeclaration | null;
    frozenCategory?: FrozenCategory | null;
}

/** Wrapper response returned by the `getReportFullExpenses` AppSync query. */
export interface FullExpenseDetailResponse {
    reportNumber: string;
    submittedBy: string;
    submittedAt?: string | null;
    items: FullExpenseDetail[];
}

/** Raw item returned by `getReportFullExpenses` before the data layer applies local status
 * overrides. The `status` field is intentionally absent — the query does not request it — and
 * `reportNumber` / `submittedBy` live on the wrapper, not per-item. */
export type RawFullExpenseItem = Omit<FullExpenseDetail, "status" | "reportNumber" | "submittedBy">;

/** Wrapper response for `getReportFullExpenses` as received from the network.
 * Items are typed as `RawFullExpenseItem` because the query omits `status`; the data layer
 * derives status from `expenseStates` on the parent report and constructs a full
 * `FullExpenseDetail` only after applying those overrides. */
export interface RawFullExpenseDetailResponse {
    reportNumber: string;
    submittedBy: string;
    submittedAt?: string | null;
    items: RawFullExpenseItem[];
}

export interface ReceiptLineItem {
    description: string;
    amount: number;
}

export interface ReceiptDetail {
    itemId: string;
    emailSubject: string;
    emailFrom: string;
    emailTo: string;
    emailDate: string;
    lineItems: ReceiptLineItem[];
    subtotal: number;
    tax: number;
    tip: number;
    total: number;
}

export interface ReportsSummary {
    reports: ExpenseReport[];
    currencyCode?: string;
}

export interface ItemsSummary {
    items: ExpenseItem[];
    currencyCode?: string;
}

// ─── Report Expenses (per-report lazy query) ──────────────────────────────────

/** Category snapshot embedded in an expense once its report is approved. */
export interface FrozenCategory {
    accountCode: string | null;
    categoryId: string | null;
    categoryName: string | null;
    legacyId: number | null;
}

/** Raw item shape returned by the `reportExpenses` AppSync query. */
export interface ReportExpenseItemRaw {
    id: string;
    reportId: string;
    status: ItemStatus;
    category: string;
    subCategoryId?: number | null;
    merchant: string;
    hasReceipt: boolean;
    receiptMissing: boolean;
    settleDate: string;
    paymentMethod: { method: string; cardKey: string | null; display: { brand: string | null; last4: string | null; description: string | null } } | null;
    amount: number;
    currencyCode?: string;
    violations: PolicyViolation[];
    generatedBy?: string | null;
    lostReceiptDeclaration?: LostReceiptDeclaration | null;
    frozenCategory?: FrozenCategory | null;
}

/** Network-level item shape; `status` is absent because the query does not select it. */
export type ReportExpenseItemNetwork = Omit<ReportExpenseItemRaw, "status">;

/** Item shape after the UI injects the `isEditable` permission flag. */
export interface ReportExpenseItem extends ReportExpenseItemRaw {
    isEditable: boolean;
}

/** Full response shape of the `reportExpenses` AppSync query. */
export interface ReportExpensesData {
    items: ReportExpenseItemRaw[];
    summary: { totalCount: number; totalAmount: number; currencyCode?: string };
}
